'use client'
import type { Dispatch, FC, ReactNode, SetStateAction } from 'react'
import { createContext, useCallback, useState } from 'react'
import { useParams, usePathname, useRouter } from 'next/navigation'
import type { TypeCmsProductCategoryFilter, TypeLang } from '@/schema'
import { type TypeParsedUrlSearchParams, getActiveFilters } from '@/utils'

export type TypeActiveFilter = {
  [key:string]: {
    [key: string]: boolean
  }
}

type TypeContext = {
  qValue: string
  setQValue: Dispatch<SetStateAction<string>>

  rawFilters: TypeCmsProductCategoryFilter[],
  setRawFilters: Dispatch<SetStateAction<TypeCmsProductCategoryFilter[]>>

  activeFilters: TypeActiveFilter,
  setActiveFilters: Dispatch<SetStateAction<TypeActiveFilter>>

  disabledFilters: TypeActiveFilter,
  setDisabledFilters: Dispatch<SetStateAction<TypeActiveFilter>>

  totalResultsCount?: number
  setTotalResultsCount?: Dispatch<SetStateAction<number>>

  updateURLWithFilters: (groupedActiveFilters) => void
  setActiveFiltersUsingRawFilters: (searchParams) => void
}

export const FiltersContext = createContext<TypeContext | null>(null)

interface Props {
  children: ReactNode
  initialRawFilters: TypeContext['rawFilters']
  initialQValue: TypeContext['qValue']
  initialActiveFilters: TypeContext['activeFilters']
}

const getIncomingParams = (groupedActiveFilters) => {
  const incomingParams = new URLSearchParams()
  // Commented since we don't have on load pagination with infinite scrolling
  console.log('🟡 getIncomingParams groupedActiveFilters', groupedActiveFilters)
  // incomingParams.set('page', '1')
  const filterKeynamesArr = groupedActiveFilters ? Object.keys(groupedActiveFilters) : []
  if (filterKeynamesArr.length > 0) {
    filterKeynamesArr.forEach(key => {
      const filterValues: [string] = groupedActiveFilters[key]

      const encodedValues = filterValues.map(value => encodeURIComponent(value)).join(',')
      incomingParams.set(key, encodedValues)
    })
  }

  return incomingParams.toString().replace(/%2C/g, ',')
}

export const FiltersProvider: FC<Props> = ({ children, initialRawFilters, initialQValue, initialActiveFilters }) => {
  const [rawFilters, setRawFilters] = useState<TypeContext['rawFilters']>(initialRawFilters ?? null)
  const [activeFilters, setActiveFilters] = useState<TypeContext['activeFilters']>(initialActiveFilters ?? {})
  const [disabledFilters, setDisabledFilters] = useState<TypeContext['activeFilters']>(null)
  const [qValue, setQValue] = useState<TypeContext['qValue']>(initialQValue ?? '')
  const [totalResultsCount, setTotalResultsCount] = useState<TypeContext['totalResultsCount']>(null)

  const pathname = usePathname()
  const { lang }: { lang: TypeLang } = useParams()
  const { replace } = useRouter()

  const updateURLWithFilters = useCallback((groupedActiveFilters) => {
    const incomingParamsAsString = getIncomingParams(groupedActiveFilters)
    const newUrl = `${pathname}?${incomingParamsAsString}`
    replace(newUrl)
    window.history.pushState({ path: newUrl }, '', newUrl)
  }, [pathname, replace])

  const setActiveFiltersUsingRawFilters = useCallback((parsedUrlSearchParams: TypeParsedUrlSearchParams) => {
    console.log('🟡 setActiveFiltersUsingRawFilters', parsedUrlSearchParams)
    const incomingActiveFilters = getActiveFilters(rawFilters, parsedUrlSearchParams, lang)
    setActiveFilters(incomingActiveFilters)
    if (parsedUrlSearchParams.q) setQValue(parsedUrlSearchParams.q[0])
  }, [rawFilters, setActiveFilters, lang])

  return (
    <FiltersContext.Provider value={{
      rawFilters,
      setRawFilters,
      activeFilters,
      setActiveFilters,
      disabledFilters,
      setDisabledFilters,
      totalResultsCount,
      setTotalResultsCount,
      qValue,
      setQValue,
      updateURLWithFilters,
      setActiveFiltersUsingRawFilters
    }}>
      {children}
    </FiltersContext.Provider>
  )
}
